import React from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import { useIntl } from 'react-intl';
import { List } from '@fuww/library/src/List/index';
import {
  BrandListItem,
  BrandListItemContent,
} from '@fuww/library/src/List/BrandList';
import { DirectoryPage, DirectoryLayout } from '../components/DirectoryPage';
import ImageWithUrls from '../components/ImageWithUrls';
import withApollo from '../lib/apollo';
import profilesConnectionQuery
from '../lib/queries/products/profilesConnectionQuery.graphql';
import withQuery from '../lib/withQuery';
import loadMore from '../lib/queries/loadMore';
import LoadMore from '../components/LoadMore';
import LinkAndAnchor from '../components/LinkAndAnchor';
import messages from '../lib/messages.json';
import redirectRouteToDomain from '../lib/redirectRouteToDomain';
import { directoryListImageConfigurations } from '../lib/imageConfigurations';

export const headerImageUrls = {
  mobile: [
    'https://r.fashionunited.com/_yuFKsxAQ6EnpEzx9BD5u6YPOhDBT4l0CzxYxxaUwYs/resize:fill:42:34:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb21wYW55X2RpcmVjdG9yeV9iYW5uZXJfbW9iaWxlLmpwZw',
    'https://r.fashionunited.com/odqFznubs0SC5DSYaIfpPXlpOurw4zvZ1lTlxTxaflQ/resize:fill:360:288:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb21wYW55X2RpcmVjdG9yeV9iYW5uZXJfbW9iaWxlLmpwZw',
    'https://r.fashionunited.com/uoraIs-8Ac2MNF3va22REZzzUTDoad_x2KHpn2p9GhM/resize:fill:720:576:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb21wYW55X2RpcmVjdG9yeV9iYW5uZXJfbW9iaWxlLmpwZw',
  ],
  desktop: [
    'https://r.fashionunited.com/1D6Qkgys8AXSg-5ijvum4_4K24vjleZlQLVoGRW-GAg/resize:fill:42:8:0/gravity:ce/quality:10/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb21wYW55X2RpcmVjdG9yeV9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/0IniqInSRjBZefGqNR9R-dnOb5wO34hx9JZxLpHMtUk/resize:fill:1350:277:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb21wYW55X2RpcmVjdG9yeV9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/HxR01CLMW6NSdgHm91tm073qPK02-mayQH7_uy8TjrE/resize:fill:2560:526:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb21wYW55X2RpcmVjdG9yeV9iYW5uZXJfZGVza3RvcC5qcGc',
    'https://r.fashionunited.com/Qk3tRYKAijRsqB0FVJV4dYOcaXxJXF7-aCUL8vuulrA/resize:fill:5120:1052:0/gravity:ce/quality:70/aHR0cHM6Ly9tZWRpYS5mYXNoaW9udW5pdGVkLmNvbS9tZWRpYS9pbWFnZXMvZmFzaGlvbl9jb21wYW55X2RpcmVjdG9yeV9iYW5uZXJfZGVza3RvcC5qcGc',
  ],
};

export const CompaniesList = ({
  data, variables, fetchMore, loading,
}) => {
  const { profilesConnection: { edges, pageInfo: { hasNextPage } } } = data;

  return (
    <>
      <List className="fu-mdc-list">
        {edges && edges.map(({
          node: {
            id,
            page: {
              slug,
              premium,
            },
            company: {
              country,
              logoUrls,
              fullName,
            },
          },
        }) => {
          if (!slug) {
            return null;
          }
          return (
            <BrandListItem key={id} premium={premium}>
              <LinkAndAnchor
                params={{ slug }}
                route="company-profile"
              >
                <BrandListItemContent
                  title={fullName}
                  premium={premium}
                  country={country}
                  logo={
                    logoUrls[0] && (
                      <ImageWithUrls
                        imageUrls={logoUrls}
                        srcSet={`${logoUrls[1]
                        } 60w, ${logoUrls[2]
                        } 120w, ${logoUrls[3]
                        } 180w`}
                        title={fullName}
                        alt={fullName}
                        width="60px"
                        height="auto"
                        objectFit="cover"
                        sizes="60px"
                        disableRoundedCorners
                      />
                    )
                  }
                />
              </LinkAndAnchor>
            </BrandListItem>
          );
        })}
      </List>
      <LoadMore
        hasNextPage={hasNextPage}
        loading={loading}
        fetchMore={
          () => loadMore({ variables })(data, 'profilesConnection', fetchMore)
        }
      />
    </>
  );
};

CompaniesList.propTypes = {
  variables: PropTypes.shape({}).isRequired,
  loading: PropTypes.bool.isRequired,
  fetchMore: PropTypes.func.isRequired,
  data: PropTypes.shape({
    profilesConnection: PropTypes.shape({
      pageInfo: PropTypes.shape({
        hasNextPage: PropTypes.bool,
      }),
      edges: PropTypes.arrayOf(
        PropTypes.shape({}),
      ),
    }),
  }).isRequired,
};

const CompaniesListWithQuery = withQuery(CompaniesList);

export const CompanyDirectoryPage = () => {
  const { query: { search, section, country } } = useRouter();
  const intl = useIntl();

  const COMPANYLIMIT = 20;

  return (
    <DirectoryPage
      title={intl.formatMessage(messages['companies-directory.title'])}
      route="company-directory"
      pageType="companies"
      ComponentWithQuery={CompaniesListWithQuery}
      query={profilesConnectionQuery}
      variables={{
        search,
        first: COMPANYLIMIT,
        imageConfigurations: directoryListImageConfigurations,
        logoConfigurations: directoryListImageConfigurations,
        premium: section !== 'all' || null,
        country: country || null,
      }}
    />
  );
};

CompanyDirectoryPage.getLayout = (page) => (
  <DirectoryLayout
    imageUrls={headerImageUrls}
    alt="FashionUnited Company Directory"
  >
    {page}
  </DirectoryLayout>
);

CompanyDirectoryPage.getInitialProps = redirectRouteToDomain(
  'company-directory',
);

export default withApollo(CompanyDirectoryPage);
